<script setup lang="ts">
import WrIcon from "~/components/global/WrIcon.vue";
import WrSVGIcon from "~/components/global/WrSVGIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== 'dark');

interface CardItem {
  title: string;
  description: string;
  stepNumber?: number;
  stepSvgDark?: string;
  stepSvgLight?: string;
  svgIcon?: string;
  icon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: CardItem[];
    ctaText?: string;
    ctaLink?: string | null;
    sectionContainerClass?: string;
    ctaContainerClass?: string;
    ctaAppendClass?: string;
  }>(),
  {
    ctaText: '',
    ctaLink: null,
    sectionContainerClass: 'mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]',
    ctaContainerClass: 'h-[40px] lg:h-[48px]',
    ctaAppendClass: 'text-sm lg:font-normal px-3 lg:px-5',
  }
);

defineEmits<{
  'cta-clicked': [];
}>();
</script>

<template>
  <section :class="sectionContainerClass" :aria-label="heading">
    <h2
      class="w-full max-w-full text-[28px] lg:text-[48px] font-semibold leading-normal text-center px-4 font-avant text-transparent bg-clip-text bg-gradient-to-r from-[#12DBFF] to-[#BA55FE]"
    >
      {{ heading }}
    </h2>
    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 text-center px-4 font-avant"
      :class="isLight ? 'text-[#000000]' : 'text-[#D9D9D9]'"
    >
      {{ subcopy }}
    </p>

    <!-- Cards grid - single responsive grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mt-10 lg:mt-16 px-4 max-w-[1200px] mx-auto">
      <article
          v-for="(card, index) in cards"
          :key="index"
          class="rounded-[8px] p-5 sm:p-6 flex flex-col overflow-hidden border font-avant"
          :class="isLight
            ? 'border-[#E7CFFF] bg-[linear-gradient(32deg,#FFF_-74.45%,#FFF_100.91%)]'
            : 'border-[#3D1B60] bg-[linear-gradient(32deg,#2B0152_-74.45%,#12002C_100.91%)]'"
        >
          <!-- Icon or step number -->
          <slot :name="`icon-${index}`" :index="index" :card="card">
            <WrIcon
              v-if="card.svgIcon && card.svgIcon.startsWith('streamline:')"
              :name="card.svgIcon"
              class="size-6 sm:size-8 text-[#501D71] dark:text-white"
            />
            <WrSVGIcon
              v-else-if="card.svgIcon"
              :name="card.svgIcon"
              class="w-10 h-10 size-6 sm:size-8 dark:text-white text-[#501D71]"
            />
            <NuxtImg
              v-else-if="card.stepSvgDark && card.stepSvgLight"
              :src="isLight ? card.stepSvgLight : card.stepSvgDark"
              :alt="'Step ' + (index + 1)"
              class="w-[71px] h-[62px] object-contain"
            />
            <span
              v-else
              class="font-avant text-[72px] font-semibold text-transparent"
              :class="isLight ? '[-webkit-text-stroke:1.5px_rgba(97,63,170,0.5)]' : '[-webkit-text-stroke:1.5px_rgba(255,255,255,0.5)]'"
            >
              {{ index + 1 }}
            </span>
          </slot>

          <h3
            class="font-semibold font-avant text-[22px] leading-normal mt-2"
            :class="isLight ? 'text-[#613FAA]' : 'text-[#F0DAFF]'"
          >
            {{ card.title }}
          </h3>
          <p
            class="mt-2 font-avant text-[15px] leading-5"
            :class="isLight ? 'text-black' : 'text-white/70'"
          >
            {{ card.description }}
          </p>
        </article>
    </div>

    <!-- CTA Button -->
    <div v-if="ctaText" class="flex justify-center mt-10">
      <WrButton
        @click="$emit('cta-clicked')"
        :container-class="ctaContainerClass"
        :append-class="ctaAppendClass"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
